import { LABEL_PRIORITY_KIND } from '@/shared/domain/labelPriority';
import { TaskType } from '@/shared/domain/taskTypes';

export { LABEL_PRIORITY_KIND } from '@/shared/domain/labelPriority';

/**
 * "What matters most for these labels?" select (Business Goals, single-label
 * classification with exactly two labels). The five options collapse into a
 * `{ kind, label }` choice: `equal` (label null) or a kind about one of the
 * two labels. Options 2–5 also decide the fallback label behind the scenes:
 * the label the choice is *not* about.
 */

/** The select only applies to single-label classification with exactly two labels. */
export const labelPriorityAppliesTo = ({ classificationType, labels } = {}) =>
    classificationType === 'single' && Array.isArray(labels) && labels.length === 2;

/** Stable select value for a choice (options are keyed by kind + label). */
export const labelPriorityValue = (labelPriority) => {
    if (!labelPriority?.kind) return null;
    if (labelPriority.kind === LABEL_PRIORITY_KIND.equal) return LABEL_PRIORITY_KIND.equal;
    return `${labelPriority.kind}:${labelPriority.label}`;
};

/**
 * The five options, in display order, for a `[first, second]` label pair.
 * Each carries the `{ kind, label }` choice it stands for.
 */
export const buildLabelPriorityOptions = (labels = []) => {
    const [first, second] = labels;
    const perLabel = (label) => [
        {
            choice: { kind: LABEL_PRIORITY_KIND.dontMiss, label },
            label: `Don't miss occurrences of "${label}"`,
        },
        {
            choice: { kind: LABEL_PRIORITY_KIND.avoidFalseDetections, label },
            label: `Avoid false detections of "${label}"`,
        },
    ];
    return [
        {
            choice: { kind: LABEL_PRIORITY_KIND.equal, label: null },
            label: 'Both labels are equally important',
        },
        ...perLabel(first),
        ...perLabel(second),
    ].map((option) => ({ ...option, value: labelPriorityValue(option.choice) }));
};

/** A choice is valid for a label pair when it is `equal` or about one of them. */
export const isLabelPriorityValid = (labelPriority, labels = []) => {
    if (!labelPriority?.kind) return false;
    if (labelPriority.kind === LABEL_PRIORITY_KIND.equal) return true;
    return (
        Object.values(LABEL_PRIORITY_KIND).includes(labelPriority.kind) &&
        labels.includes(labelPriority.label)
    );
};

/**
 * Fallback label implied by a choice: the *other* label for options 2–5,
 * `null` for `equal` (the user picks the fallback explicitly) or an invalid
 * choice.
 */
export const derivedFallbackLabel = (labelPriority, labels = []) => {
    if (!isLabelPriorityValid(labelPriority, labels)) return null;
    if (labelPriority.kind === LABEL_PRIORITY_KIND.equal) return null;
    return labels.find((label) => label !== labelPriority.label) ?? null;
};

/**
 * Keeps a `labelPriority` choice consistent with the task type / labels a
 * form edit resolves to, and the fallback label in lockstep with it — the
 * one rule behind both the Business Goals step and the Edit Task modal:
 *
 * - the section stopped applying (type / label count) or the choice's label
 *   is gone: drop the choice, and the fallback it derived unless the user
 *   picked that fallback explicitly (`fallbackLabelUserSet`);
 * - options 2–5 ("don't miss" / "avoid false detections" of a label): pin
 *   the fallback to the other label and mark it auto-managed;
 * - "equally important" / no choice: leave the fallback to the visible
 *   Fallback select.
 *
 * Pass the values the edit resolves to (already merged with the patch).
 * Returns the fields to override; `{}` when nothing changes.
 */
export const reconcileLabelPriority = ({
    taskType,
    classificationType,
    labels = [],
    labelPriority,
    fallbackLabelUserSet = false,
}) => {
    if (!labelPriority) return {};
    const applies =
        taskType === TaskType.classification && labelPriorityAppliesTo({ classificationType, labels });
    if (!applies || !isLabelPriorityValid(labelPriority, labels)) {
        return fallbackLabelUserSet ? { labelPriority: null } : { labelPriority: null, fallbackLabel: null };
    }
    if (labelPriority.kind !== LABEL_PRIORITY_KIND.equal) {
        return { fallbackLabel: derivedFallbackLabel(labelPriority, labels), fallbackLabelUserSet: false };
    }
    return {};
};
